<script setup lang="ts">
import type { MegamenuLink, MegamenuSection } from '~/data/megamenu'
import { sendMenuClick } from '~/composables/useGtmEvents'
import { isExternalUrl } from '~/utils/urlHelpers'

const props = defineProps<{
  section: MegamenuSection | null
  menuName: string
}>()

const { baseUrl } = useRuntimeConfig().public as { baseUrl: string }
const resolveHref = useFullLinkUrl()
const isExternal = (href: string) => isExternalUrl(href, baseUrl)

/** Flatten subSections into column groups for layout */
const columnGroups = computed(() => {
  if (!props.section?.subSections?.length) return null
  return props.section.subSections.map((sub) => sub.items).filter(Boolean)
})

const flatItems = computed(() => {
  if (!props.section) return []
  if (props.section.items?.length)
    return props.section.items.filter((i) => i?.label)
  return []
})

function handleSubSectionItemClick(
  subSection: { title: string; items: MegamenuLink[] },
  item: MegamenuLink
) {
  if (!props.section || !item.href) return
  sendMenuClick({
    menuName: props.menuName,
    sectionTitle: props.section.title,
    subSectionTitle: subSection.title,
    itemLabel: item.label,
    itemPath: item.href,
  })
}

function handleFlatItemClick(item: MegamenuLink) {
  if (!props.section || !item.href) return
  sendMenuClick({
    menuName: props.menuName,
    sectionTitle: props.section.title,
    itemLabel: item.label,
    itemPath: item.href,
  })
}
</script>

<template>
  <Transition name="megamenu-fade" mode="out-in">
    <div
      v-if="section"
      :key="section.title"
      data-test-id="megamenu-dropdown-content"
      class="py-4 grow"
    >
      <!-- Column groups from subSections (e.g. Top categories: Bestseller, Kreativkurse, ...) -->
      <div
        v-if="columnGroups?.length"
        data-test-id="megamenu-dropdown-content-column-groups"
        class="grid gap-6 pl-8"
        :class="[
          columnGroups.length === 2 && 'grid-cols-2',
          columnGroups.length === 3 && 'grid-cols-3',
          columnGroups.length === 4 && 'grid-cols-4',
        ]"
      >
        <ul
          v-for="(subSection, subSectionIndex) in section?.subSections"
          :key="subSectionIndex"
          :data-test-id="`megamenu-dropdown-content-subsection-${subSectionIndex}`"
          class="flex flex-col gap-4 text-sm sub-section"
          :aria-label="subSection?.title"
        >
          <li
            v-for="(item, i) in subSection?.items"
            :key="i"
            :data-test-id="`megamenu-dropdown-content-subsection-${subSectionIndex}-item-${i}`"
          >
            <a
              v-if="item.href && isExternal(item.href)"
              :href="resolveHref(item.href)"
              class="text-stone-800 hover:text-teal-800 font-normal no-underline hover:underline"
              target="_blank"
              rel="noopener"
              @click="handleSubSectionItemClick(subSection, item)"
            >
              {{ item.label }}
            </a>
            <NuxtLinkLocale
              v-else-if="item.href"
              :to="resolveHref(item.href)"
              class="text-stone-900 hover:text-teal-600 font-normal no-underline"
              @click="handleSubSectionItemClick(subSection, item)"
            >
              {{ item.label }}
            </NuxtLinkLocale>
            <span v-else class="text-stone-500">{{ item.label }}</span>
          </li>
        </ul>
      </div>
      <!-- Single list (items) -->
      <ul
        v-else-if="flatItems.length"
        data-test-id="megamenu-dropdown-content-items"
        class="grid gap-x-8 gap-y-4 pl-8 text-sm"
        :class="[
          section?.columns === 2 && 'grid-cols-2',
          section?.columns === 3 && 'grid-cols-3',
          section?.columns === 4 && 'grid-cols-4',
        ]"
      >
        <li
          v-for="(item, i) in flatItems"
          :key="i"
          :data-test-id="`megamenu-dropdown-content-item-${i}`"
        >
          <a
            v-if="item.href && isExternal(item.href)"
            :href="resolveHref(item.href)"
            class="text-stone-800 hover:text-teal-800 font-normal no-underline hover:underline"
            target="_blank"
            rel="noopener"
            @click="handleFlatItemClick(item)"
          >
            {{ item.label }}
          </a>
          <NuxtLinkLocale
            v-else-if="item.href"
            :to="resolveHref(item.href)"
            class="text-stone-900 hover:text-teal-600 no-underline"
            @click="handleFlatItemClick(item)"
          >
            {{ item.label }}
          </NuxtLinkLocale>
          <span v-else class="text-stone-500">{{ item.label }}</span>
        </li>
      </ul>
    </div>
  </Transition>
</template>
<style scoped lang="postcss">
.megamenu-fade-enter-active,
.megamenu-fade-leave-active {
  transition: opacity 0.15s ease-out;
}
.megamenu-fade-enter-from,
.megamenu-fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .megamenu-fade-enter-active,
  .megamenu-fade-leave-active {
    transition-duration: 0.01ms;
  }
}

.sub-section:before {
  content: attr(aria-label);
  @apply text-stone-500 font-medium;
}
</style>
